(function(){
    // Utility helpers
    const normalize = (v) => (v ?? '').toString().trim().toLowerCase();
    const showElement = (el) => el && el.classList.remove('hidden');
    const hideElement = (el) => el && el.classList.add('hidden');

    // Root container (scoped to the Festival Program grid)
    let root = null;

    // State (scoped to root)
    let $selects = [];
    let $textInputs = [];
    let $numberInputs = [];
    let $multiContainers = [];
    let $sortSelect = null;

    const collectControls = () => {
        $selects = Array.from(root.querySelectorAll('[data-filter-type="select"][data-filter-field]'));
        $textInputs = Array.from(root.querySelectorAll('[data-filter-type="text"][data-filter-field]'));
        $numberInputs = Array.from(root.querySelectorAll('[data-filter-type="number"][data-filter-field]'));
        $multiContainers = Array.from(root.querySelectorAll('[data-filter-type="multi"][data-filter-field]'));
        $sortSelect = root.querySelector('[data-filter-field="sortBy"]');
    };

    const getActiveFilters = () => {
        const filters = {};
        // Selects
        $selects.forEach(sel => {
            const field = sel.getAttribute('data-filter-field');
            if (field === 'sortBy') return; // exclude sort control from filters
            const val = sel.value;
            if (val && normalize(val) !== 'any') {
                filters[field] = val;
            }
        });
        // Text search (only first for now)
        const text = $textInputs[0]?.value || '';
        if (text && text.length) {
            filters['title'] = text;
        }
        // Number inputs (price range)
        $numberInputs.forEach(inp => {
            const field = inp.getAttribute('data-filter-field');
            const val = parseFloat(inp.value);
            if (!isNaN(val)) {
                filters[field] = val;
            }
        });
        // Multi-select (checkbox) filters
        $multiContainers.forEach(container => {
            const field = container.getAttribute('data-filter-field');
            const checked = Array.from(container.querySelectorAll('input[type="checkbox"][data-multi-option]:checked'))
                .map(cb => normalize(cb.value))
                .filter(Boolean);
            if (checked.length) {
                filters[field] = checked; // array of normalized values
            }
        });
        return filters;
    };

    const splitMulti = (attrVal) => {
        if (!attrVal) return [];
        // Attributes with multi values use "|" separator
        return attrVal.split('|').map(v => normalize(v)).filter(Boolean);
    };

    // Determine if a single card matches a filter set
    const cardMatches = (card, filters) => {
        let match = true;

        // Handle numeric price range first if present
        const hasMin = Object.prototype.hasOwnProperty.call(filters, 'price-min');
        const hasMax = Object.prototype.hasOwnProperty.call(filters, 'price-max');
        if (hasMin || hasMax) {
            const minFilter = hasMin ? parseFloat(filters['price-min']) : NaN;
            const maxFilter = hasMax ? parseFloat(filters['price-max']) : NaN;

            // Derive card price range
            let cardMin = parseFloat(card.getAttribute('data-price-min') || '');
            let cardMax = parseFloat(card.getAttribute('data-price-max') || '');
            const isFree = (normalize(card.getAttribute('data-ticket-type')) === 'free') || (normalize(card.getAttribute('data-price')) === 'free');

            if (isNaN(cardMin) && isFree) cardMin = 0;
            if (isNaN(cardMax) && isFree) cardMax = 0;

            // If still NaN, treat as unknown pricing.
            // Include unknown-priced cards unless a positive min is set.
            if ((hasMin || hasMax) && (isNaN(cardMin) && isNaN(cardMax))) {
                if (hasMin && !isNaN(minFilter) && minFilter > 0) {
                    match = false; // exclude only when user demands min > 0
                } else {
                    // include when no min provided, or min = 0 (regardless of max-only filters)
                }
            } else {
                if (hasMin && !isNaN(minFilter)) {
                    // Exclude cards whose max is below the min filter
                    if (!isNaN(cardMax) && cardMax < minFilter) match = false;
                }
                if (match && hasMax && !isNaN(maxFilter)) {
                    // Exclude cards whose min is above the max filter
                    if (!isNaN(cardMin) && cardMin > maxFilter) match = false;
                }
            }
        }

        if (!match) return false;

        for (const [field, value] of Object.entries(filters)) {
            if (!match) break;
            if (field === 'price-min' || field === 'price-max') continue; // already processed

            if (field === 'title') {
                const haystack = normalize(card.getAttribute('data-title'));
                const needle = normalize(value);
                match = haystack.includes(needle);
                continue;
            }

            const attrName = `data-${field}`;
            const rawVal = card.getAttribute(attrName) || '';
            const list = splitMulti(rawVal);

            // Support arrays (multi) and strings
            if (Array.isArray(value)) {
                // OR logic: match if any selected value is present in the card list
                const selected = value.map(v => normalize(v));
                match = selected.some(v => list.includes(v));
            } else {
                const normVal = normalize(value);
                if (field === 'ages' || field === 'date' || field === 'genre') {
                    match = list.includes(normVal);
                } else {
                    match = normalize(rawVal) === normVal;
                }
            }
        }
        return match;
    };

    // Rebuild dropdown options based on current cards
    const rebuildDropdownOptions = () => {
        const cards = Array.from(root.querySelectorAll('.event-card'));

        $selects.forEach(sel => {
            const field = sel.getAttribute('data-filter-field');
            if (field === 'sortBy' || field === 'price' || field === 'ticket-type') return;

            // Collect all unique values from current cards
            const values = new Set();
            cards.forEach(card => {
                const attrName = `data-${field}`;
                const rawVal = card.getAttribute(attrName) || '';

                // Split multi-values but preserve original case for display
                const rawList = rawVal ? rawVal.split('|').map(v => v.trim()).filter(Boolean) : [];
                rawList.forEach(val => {
                    if (val && normalize(val) !== 'any') {
                        values.add(val);
                    }
                });
            });

            // Remember current selection
            const currentValue = sel.value;

            // Clear existing options except the first "Any" option
            const firstOption = sel.options[0];
            sel.innerHTML = '';
            if (firstOption) {
                sel.appendChild(firstOption);
            }

            // Add new options based on current cards
            const sortedValues = Array.from(values).sort();
            sortedValues.forEach(value => {
                const option = document.createElement('option');
                option.value = value;

                // Format display text based on field type
                if (field === 'date') {
                    // Format date like "September 6" to match template formatting
                    try {
                        const date = new Date(value + 'T00:00:00');
                        option.textContent = date.toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric'
                        });
                    } catch (e) {
                        option.textContent = value; // fallback to raw value
                    }
                } else {
                    option.textContent = value;
                }

                sel.appendChild(option);
            });

            // Restore selection if it still exists, otherwise reset to "Any"
            if (currentValue && Array.from(sel.options).some(opt => opt.value === currentValue)) {
                sel.value = currentValue;
            } else {
                sel.selectedIndex = 0; // Reset to "Any"
            }
        });
    };

    // Disable/hide options that would yield no results given current selections
    const updateOptionStates = () => {
        const activeFilters = getActiveFilters();
        const cards = Array.from(root.querySelectorAll('.event-card'));

        $selects.forEach(sel => {
            const field = sel.getAttribute('data-filter-field');
            if (field === 'sortBy') return; // do not compute availability for sort options
            if (field === 'price' || field === 'ticket-type') return;  // always allow switching Ticket Type (Free/Paid)
            const options = Array.from(sel.options);

            options.forEach((opt, idx) => {
                const label = (opt.textContent || '').trim();
                const hasValue = !!opt.value; // value attribute may be empty for "Any"
                const isAny = normalize(opt.value || label) === 'any' || idx === 0 && !hasValue;

                // Always keep Any enabled and visible
                if (isAny) {
                    opt.disabled = false;
                    opt.hidden = false;
                    return;
                }

                const val = opt.value || label;
                // Build test filters = active filters except this field, plus this option value
                const test = { ...activeFilters };
                delete test[field];
                if (val && normalize(val) !== 'any') {
                    test[field] = val;
                }

                const anyMatch = cards.some(card => cardMatches(card, test));

                // Gray out (disable) and optionally hide options that produce zero results
                opt.disabled = !anyMatch;
                opt.hidden = !anyMatch; // temporarily remove from dropdown as requested
            });
        });
    };

    // Helpers for parsing dates
    const parseIsoDate = (s) => {
        if (!s) return NaN;
        // Ensure time component to avoid timezone shifts
        const d = new Date(`${s}T00:00:00`);
        return d.getTime();
    };

    const getUpcomingSortKey = (card) => {
        const listRaw = card.getAttribute('data-date') || '';
        const parts = listRaw.split('|').filter(Boolean);
        const todayMs = new Date(); todayMs.setHours(0,0,0,0);
        const today = todayMs.getTime();

        let nextFuture = Infinity;
        let lastPast = -Infinity;

        for (const p of parts) {
            const t = parseIsoDate(p);
            if (isNaN(t)) continue;
            if (t >= today) {
                if (t < nextFuture) nextFuture = t;
            } else {
                if (t > lastPast) lastPast = t;
            }
        }
        const hasFuture = nextFuture !== Infinity;
        return { hasFuture, nextFuture, lastPast };
    };

    const getSortValue = (card, mode) => {
        switch (mode) {
            case 'date-upcoming': {
                return getUpcomingSortKey(card);
            }
            case 'date-newest': {
                const created = parseInt(card.getAttribute('data-created') || '0', 10) || 0;
                return created;
            }
            // Fallbacks for any older modes if still present
            case 'date-asc':
            case 'date-desc': {
                const d = card.getAttribute('data-date-first') || '';
                return d || '';
            }
            case 'title-az':
            case 'title-za': {
                const t = card.getAttribute('data-title-sort') || '';
                return t.toLowerCase();
            }
            case 'venue-az':
            case 'venue-za': {
                const v = card.getAttribute('data-venue') || '';
                return v.toLowerCase();
            }
            case 'price-free-first': {
                const p = (card.getAttribute('data-ticket-type') || '').toLowerCase();
                return p === 'free' ? 0 : 1;
            }
            default:
                return '';
        }
    };

    const applySort = (cards) => {
        const grid = root.querySelector('#events-container');
        if (!grid) return;
        const mode = $sortSelect?.value || 'date-upcoming';

        const sorted = cards.slice().sort((a, b) => {
            const av = getSortValue(a, mode);
            const bv = getSortValue(b, mode);

            if (mode === 'date-upcoming') {
                // Future events first (today included), by their next upcoming date asc
                // Past-only events after, by most recent past desc
                const aHasFuture = av.hasFuture ? 0 : 1;
                const bHasFuture = bv.hasFuture ? 0 : 1;
                if (aHasFuture !== bHasFuture) return aHasFuture - bHasFuture;
                if (av.hasFuture && bv.hasFuture) {
                    if (av.nextFuture === bv.nextFuture) return 0;
                    return av.nextFuture < bv.nextFuture ? -1 : 1;
                }
                // both past-only
                if (av.lastPast === bv.lastPast) return 0;
                return av.lastPast > bv.lastPast ? -1 : 1; // recent past first
            }
            if (mode === 'date-newest') {
                if (av === bv) return 0;
                return av > bv ? -1 : 1; // newest (largest timestamp) first
            }
            // legacy fallbacks if any of those modes appear
            if (mode === 'date-asc' || mode === 'date-desc') {
                if (av === bv) return 0;
                const cmp = av < bv ? -1 : 1;
                return mode === 'date-asc' ? cmp : -cmp;
            }
            if (mode === 'title-az' || mode === 'venue-az') {
                if (av === bv) return 0;
                return av < bv ? -1 : 1;
            }
            if (mode === 'title-za' || mode === 'venue-za') {
                if (av === bv) return 0;
                return av > bv ? -1 : 1;
            }
            if (mode === 'price-free-first') {
                if (av === bv) return 0;
                return av - bv;
            }
            return 0;
        });

        // Re-append in the new order (keeps hidden state intact)
        sorted.forEach(card => grid.appendChild(card));
    };

    const applyFilters = () => {
        const filters = getActiveFilters();
        const cards = Array.from(root.querySelectorAll('.event-card'));

        // Fast path: if there are no active filters at all, show everything
        if (Object.keys(filters).length === 0) {
            cards.forEach(card => showElement(card));
            applySort(cards);
            const noEventsState = root.querySelector('#no-events-state');
            if (noEventsState) {
                noEventsState.classList.add('hidden');
                noEventsState.style.display = 'none';
            }
            updateOptionStates();
            return;
        }

        cards.forEach(card => {
            const show = cardMatches(card, filters);
            if (show) {
                showElement(card);
            } else {
                hideElement(card);
            }
        });

        // After filtering, sort the full list so visible ones appear in order
        applySort(cards);

        // Optional: toggle an empty state element if present
        const grid = root.querySelector('#events-container');
        const visibleCount = grid ? grid.querySelectorAll('.event-card:not(.hidden)').length : 0;
        const noEventsState = root.querySelector('#no-events-state');
        if (noEventsState) {
            if (visibleCount === 0) {
                noEventsState.classList.remove('hidden');
                noEventsState.style.display = 'flex'; // ensure visible even if inline style was set by hub loader
            } else {
                noEventsState.classList.add('hidden');
                noEventsState.style.display = 'none';
            }
        }

        // After applying filters, update option availability
        updateOptionStates();
    };

    // Simple debounce utility (to avoid lodash dependency)
    const debounce = (fn, wait = 250) => {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(null, args), wait);
        };
    };
    // module-level debounced applier so slider logic can reuse it
    const applyFiltersDebounced = debounce(() => applyFilters(), 300);

    const bindEvents = () => {
        // Change handlers
        // When non-price filters change, clear price inputs and recompute bounds so we don't lock invalid price points
        const nonPriceSelects = $selects.filter(sel => sel.getAttribute('data-filter-field') !== 'sortBy');
        nonPriceSelects.forEach(sel => {
            sel.addEventListener('change', () => {
                const field = sel.getAttribute('data-filter-field');
                const val = sel.value;
                // no-op
                // Clear any existing price inputs on any non-sort change
                $numberInputs.forEach(inp => inp.value = '');
                initPriceSlider();
                applyFilters();
            });
        });
        if ($sortSelect) {
            $sortSelect.addEventListener('change', applyFilters);
        }
        $textInputs.forEach(inp => {
            inp.addEventListener('input', () => {
                $numberInputs.forEach(n => n.value = '');
                initPriceSlider();
                applyFiltersDebounced();
            });
        });
        $numberInputs.forEach(inp => {
            inp.addEventListener('input', applyFiltersDebounced);
            inp.addEventListener('change', applyFilters); // finalize on change
        });

        // Multi-select UI events
        const closeAllMulti = () => {
            $multiContainers.forEach(c => {
                const panel = c.querySelector('[data-multi-panel]');
                if (panel) panel.classList.add('hidden');
            });
        };
        $multiContainers.forEach(container => {
            const toggle = container.querySelector('[data-multi-toggle]');
            const panel = container.querySelector('[data-multi-panel]');
            const labelEl = container.querySelector('[data-multi-label]');
            const clearBtn = container.querySelector('[data-multi-clear]');
            const updateLabel = () => {
                const checked = Array.from(container.querySelectorAll('input[type="checkbox"][data-multi-option]:checked'));
                if (!checked.length) {
                    if (labelEl) labelEl.textContent = 'Any';
                    return;
                }
                if (checked.length === 1) {
                    // Show the single selected option's label/value
                    if (labelEl) labelEl.textContent = checked[0].value;
                } else {
                    // Abbreviate to avoid lengthy labels when more than one is selected
                    if (labelEl) labelEl.textContent = 'Multiple';
                }
            };
            if (toggle && panel) {
                toggle.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const isHidden = panel.classList.contains('hidden');
                    closeAllMulti();
                    if (isHidden) panel.classList.remove('hidden');
                });
            }
            container.querySelectorAll('input[type="checkbox"][data-multi-option]').forEach(cb => {
                cb.addEventListener('change', () => { updateLabel(); applyFiltersDebounced(); });
            });
            if (clearBtn) {
                clearBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    container.querySelectorAll('input[type="checkbox"][data-multi-option]').forEach(cb => { cb.checked = false; });
                    updateLabel();
                    // Also close the panel after clearing
                    if (panel) panel.classList.add('hidden');
                    applyFilters();
                });
            }
            // Initialize label once
            updateLabel();
        });
        // Click outside to close panels (close unless clicking inside an open panel)
        document.addEventListener('click', (e) => {
            if (!root.contains(e.target)) return;
            const clickedInsideOpenPanel = $multiContainers.some(c => {
                const p = c.querySelector('[data-multi-panel]');
                return p && !p.classList.contains('hidden') && p.contains(e.target);
            });
            if (!clickedInsideOpenPanel) closeAllMulti();
        });

        // Clear button
        const clearBtn = root.querySelector('[data-clear-filters]');
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                $selects.forEach(sel => {
                    if (sel.getAttribute('data-filter-field') === 'sortBy') {
                        return; // keep current sort selection
                    }
                    if (sel.options && sel.options.length) {
                        sel.selectedIndex = 0; // back to Any
                    }
                });
                $textInputs.forEach(inp => inp.value = '');
                $numberInputs.forEach(inp => inp.value = '');
                // Clear multi
                $multiContainers.forEach(container => {
                    container.querySelectorAll('input[type="checkbox"][data-multi-option]').forEach(cb => { cb.checked = false; });
                    const labelEl = container.querySelector('[data-multi-label]');
                    if (labelEl) labelEl.textContent = 'Any';
                });
                // Reset slider visuals to full span
                initPriceSlider();
                applyFilters();
            });
        }
    };

    // -------- Price slider logic --------
    let priceSliderBound = false;
    // Cache the latest computed bounds to detect hub/context changes
    let lastPriceBounds = { min: null, max: null };
    // Shared live bounds so event handlers always use the latest values
    let currentPriceBounds = { min: 0, max: 0 };

    const computePriceStats = () => {
        const allCards = Array.from(root.querySelectorAll('.event-card'));
        // Compute stats based on cards that match current filters EXCLUDING price range
        const active = getActiveFilters();
        delete active['price-min'];
        delete active['price-max'];
        const cards = allCards.filter(card => cardMatches(card, active));

        const values = [];
        let anyPaid = false;
        cards.forEach(card => {
            let min = parseFloat(card.getAttribute('data-price-min') || '');
            let max = parseFloat(card.getAttribute('data-price-max') || '');
            const isFree = normalize(card.getAttribute('data-ticket-type')) === 'free';
            if (isFree) {
                if (isNaN(min)) min = 0;
                if (isNaN(max)) max = 0;
            }
            if (!isNaN(min) && min > 0) { values.push(min); anyPaid = true; }
            if (!isNaN(max) && max > 0) { values.push(max); anyPaid = true; }
        });
        if (!values.length) {
            // No paid prices available in current valid set
            return { hasPaid: false, min: 0, max: 0 };
        }
        const paidMax = Math.max(...values);
        // Always start slider from 0 when any paid prices exist
        const min = 0;
        const max = paidMax;
        const result = { hasPaid: anyPaid, min, max };
        return result;
    };

    const getPriceContainer = () => root.querySelector('[data-price-range-container]');

    const formatCurrency = (value) => {
        const n = Number(value);
        if (isNaN(n)) return '$0';
        return `$${new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 }).format(n)}`;
    };

    // Do not write to number inputs on init; keep empty unless user interacts
    const initPriceSlider = () => {
        const container = getPriceContainer();
        if (!container) return;

        const minLabel = container.querySelector('.price-min');
        const maxLabel = container.querySelector('.price-max');
        const valueLabel = container.querySelector('.slider-value');
        const sliderMin = container.querySelector('.slider-min');
        const sliderMax = container.querySelector('.slider-max');
        const sliderTrack = container.querySelector('.slider-track');
        const inputMin = container.querySelector('[data-filter-field="price-min"]');
        const inputMax = container.querySelector('[data-filter-field="price-max"]');

        const stats = computePriceStats();

        // If bounds changed since last time, force a hard reset of inputs and thumbs
        const boundsChanged = (lastPriceBounds.min !== stats.min) || (lastPriceBounds.max !== stats.max);
        if (boundsChanged) {
            // Clear any existing values to avoid stale constraints from previous hub
            if (inputMin) inputMin.value = '';
            if (inputMax) inputMax.value = '';
        }

        const hasPaid = stats.hasPaid;
        if (!hasPaid) {
            // Hide container, clear inputs and re-apply filters
            container.classList.add('hidden');
            container.style.display = 'none';
            if (inputMin) { inputMin.value = ''; inputMin.removeAttribute('min'); inputMin.removeAttribute('max'); }
            if (inputMax) { inputMax.value = ''; inputMax.removeAttribute('min'); inputMax.removeAttribute('max'); }
            applyFilters();
            return;
        }

        // Show container and set labels/placeholders
        container.classList.remove('hidden');
        container.style.display = '';
        minLabel && (minLabel.textContent = formatCurrency(stats.min));
        maxLabel && (maxLabel.textContent = formatCurrency(stats.max));
        if (inputMin) { inputMin.setAttribute('min', String(stats.min)); inputMin.setAttribute('max', String(stats.max)); if (!inputMin.value) inputMin.placeholder = `Min ${formatCurrency(stats.min)}`; }
        if (inputMax) { inputMax.setAttribute('min', String(stats.min)); inputMax.setAttribute('max', String(stats.max)); if (!inputMax.value) inputMax.placeholder = `Max ${formatCurrency(stats.max)}`; }

        // If current values fall out of new bounds, clear them so they don't lock out valid cards
        if (inputMin && inputMin.value) {
            const v = parseFloat(inputMin.value);
            if (isNaN(v) || v < stats.min || v > stats.max) inputMin.value = '';
        }
        if (inputMax && inputMax.value) {
            const v = parseFloat(inputMax.value);
            if (isNaN(v) || v < stats.min || v > stats.max) inputMax.value = '';
        }

        // Configure slider ranges to 0..100 percentages and map onto [min, max]
        if (!sliderMin || !sliderMax || !sliderTrack) return;
        sliderMin.min = 0; sliderMin.max = 100;
        sliderMax.min = 0; sliderMax.max = 100;
        // If inputs are empty, or bounds changed, reset slider to full span; else keep previous values
        if (boundsChanged || (!inputMin?.value && !inputMax?.value)) {
            sliderMin.value = 0;
            sliderMax.value = 100;
        }

        const mapPctToVal = (pct) => {
            const { min, max } = currentPriceBounds;
            return Math.round(min + (pct / 100) * (max - min));
        };
        const mapValToPct = (val) => {
            const { min, max } = currentPriceBounds;
            return max === min ? 0 : ((val - min) / (max - min)) * 100;
        };

        const updateTrack = () => {
            const minPct = Math.min(parseFloat(sliderMin.value), parseFloat(sliderMax.value));
            const maxPct = Math.max(parseFloat(sliderMin.value), parseFloat(sliderMax.value));
            const thumbSize = 12;
            sliderTrack.style.left = `calc(${minPct}% - ${(minPct / 100) * thumbSize}px)`;
            sliderTrack.style.width = `calc(${(maxPct - minPct)}% + ${((100 - (maxPct - minPct)) / 100) * thumbSize}px)`;
        };

        const updateLabels = () => {
            const minPct = Math.min(parseFloat(sliderMin.value), parseFloat(sliderMax.value));
            const maxPct = Math.max(parseFloat(sliderMin.value), parseFloat(sliderMax.value));
            const minVal = mapPctToVal(minPct);
            const maxVal = mapPctToVal(maxPct);
            if (valueLabel) {
                valueLabel.textContent = minVal === maxVal ? `${formatCurrency(minVal)}` : `${formatCurrency(minVal)} - ${formatCurrency(maxVal)}`;
            }
        };

        const applyFromSlider = () => {
            const minPct = Math.min(parseFloat(sliderMin.value), parseFloat(sliderMax.value));
            const maxPct = Math.max(parseFloat(sliderMin.value), parseFloat(sliderMax.value));
            const minVal = mapPctToVal(minPct);
            const maxVal = mapPctToVal(maxPct);
            // Reflect into inputs and trigger filtering (debounced by input listeners)
            if (inputMin) inputMin.value = String(minVal);
            if (inputMax) inputMax.value = String(maxVal);
            applyFiltersDebounced();
        };

        const applyFromInputs = () => {
            // If inputs are empty, keep slider full span but do not filter
            const minVal = parseFloat(inputMin?.value || '');
            const maxVal = parseFloat(inputMax?.value || '');
            if (isNaN(minVal) && isNaN(maxVal)) {
                sliderMin.value = 0; sliderMax.value = 100; updateTrack(); updateLabels();
                return;
            }
            const { min: bMin, max: bMax } = currentPriceBounds;
            const minClamped = isNaN(minVal) ? bMin : Math.max(bMin, Math.min(minVal, bMax));
            const maxClamped = isNaN(maxVal) ? bMax : Math.max(bMin, Math.min(maxVal, bMax));
            sliderMin.value = String(mapValToPct(minClamped));
            sliderMax.value = String(mapValToPct(maxClamped));
            updateTrack();
            updateLabels();
        };

        // Track drag (pan) support: drag the filled track to move both thumbs together
        const sliderRange = container.querySelector('.slider-range');
        let isPanning = false;
        const panState = { startX: 0, minPct: 0, maxPct: 0, span: 0, width: 0 };

        const getClientX = (e) => (e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX);

        const onTrackMouseDown = (e) => {
            e.preventDefault();
            const rect = sliderRange.getBoundingClientRect();
            panState.width = rect.width || 0;
            if (panState.width <= 0) return;
            panState.startX = getClientX(e);
            const a = Math.min(parseFloat(sliderMin.value), parseFloat(sliderMax.value));
            const b = Math.max(parseFloat(sliderMin.value), parseFloat(sliderMax.value));
            panState.minPct = a; panState.maxPct = b; panState.span = b - a;
            isPanning = true;
            document.addEventListener('mousemove', onTrackMouseMove);
            document.addEventListener('mouseup', onTrackMouseUp);
            document.addEventListener('touchmove', onTrackMouseMove, { passive: false });
            document.addEventListener('touchend', onTrackMouseUp);
        };
        const onTrackMouseMove = (e) => {
            if (!isPanning || panState.width <= 0) return;
            e.preventDefault();
            const dx = getClientX(e) - panState.startX;
            const deltaPct = (dx / panState.width) * 100;
            const newMin = Math.max(0, Math.min(100 - panState.span, panState.minPct + deltaPct));
            const newMax = newMin + panState.span;
            sliderMin.value = String(newMin);
            sliderMax.value = String(newMax);
            updateTrack();
            updateLabels();
            // Debounced filter apply to avoid excessive recalcs
            applyFiltersDebounced();
        };
        const onTrackMouseUp = () => {
            if (!isPanning) return;
            isPanning = false;
            document.removeEventListener('mousemove', onTrackMouseMove);
            document.removeEventListener('mouseup', onTrackMouseUp);
            document.removeEventListener('touchmove', onTrackMouseMove);
            document.removeEventListener('touchend', onTrackMouseUp);
            // Finalize: sync inputs with current slider window
            applyFromSlider();
        };

        // Bind once
        if (!priceSliderBound) {
            sliderMin.addEventListener('input', () => { updateTrack(); updateLabels(); });
            sliderMax.addEventListener('input', () => { updateTrack(); updateLabels(); });
            sliderMin.addEventListener('change', applyFromSlider);
            sliderMax.addEventListener('change', applyFromSlider);
            inputMin && inputMin.addEventListener('change', () => { applyFromInputs(); applyFilters(); });
            inputMax && inputMax.addEventListener('change', () => { applyFromInputs(); applyFilters(); });
            inputMin && inputMin.addEventListener('input', applyFiltersDebounced);
            inputMax && inputMax.addEventListener('input', applyFiltersDebounced);
            // Enable dragging the track itself (both mouse and touch)
            sliderTrack.addEventListener('mousedown', onTrackMouseDown);
            sliderTrack.addEventListener('touchstart', onTrackMouseDown, { passive: false });
            priceSliderBound = true;
        }

        // Update shared and last-known bounds before rendering
        currentPriceBounds = { min: stats.min, max: stats.max };
        lastPriceBounds = { min: stats.min, max: stats.max };

        // Initial visuals
        updateTrack();
        updateLabels();
    };


    // Single-instance API (global)
    function reset() {
        if (!root) return;
        collectControls();
        rebuildDropdownOptions(); // Rebuild options based on current cards
        // Reset selects (except sortBy), text and number inputs, multi selections, slider, then apply
        $selects.forEach(sel => {
            if (sel.getAttribute('data-filter-field') === 'sortBy') return;
            if (sel.options && sel.options.length) sel.selectedIndex = 0;
        });
        $textInputs.forEach(inp => inp.value = '');
        $numberInputs.forEach(inp => inp.value = '');
        // Clear multi
        $multiContainers.forEach(container => {
            container.querySelectorAll('input[type="checkbox"][data-multi-option]').forEach(cb => { cb.checked = false; });
            const labelEl = container.querySelector('[data-multi-label]');
            if (labelEl) labelEl.textContent = 'Any';
        });
        initPriceSlider();
        applyFilters();
    }
    function refresh() {
        if (!root) return;
        collectControls();
        rebuildDropdownOptions(); // Rebuild options based on current cards
        initPriceSlider();
        applyFilters();
    }

    window.EventFilters = { reset, refresh };

    // Initialize once on window load for the first .festival-program-grid
    window.addEventListener('load', () => {
        const grid = document.querySelector('.festival-program-grid');
        if (!grid) return; // do nothing if no grid is present
        root = grid;
        collectControls();
        $numberInputs.forEach(inp => { try { inp.value = ''; } catch(e) {} });
        bindEvents();
        initPriceSlider();
        applyFilters();
    });
})();